import React, { useState, useEffect } from "react";
import { PatientProfile } from "./components/PatientProfile/PatientProfile";
import ViewDocumentModal from "../ui/ViewDocumentModal";
import {
  defaultPatientData,
  defaultActiveConsultation,
  availablePharmacists,
  type PatientInfo,
  type ActiveConsultation,
} from "@/lib/dashboard/patientDashboardData";
import {
  useAllergiesState,
  type Allergy,
  savePatientAllergy,
  updatePatientAllergy,
  deletePatientAllergy,
} from "@/lib/dashboard/allergiesData";
import {
  type Condition,
  savePatientCondition,
  useMedicalConditionsState,
  updatePatientCondition,
  deletePatientCondition,
} from "@/lib/dashboard/conditionsData";
import {
  defaultMedications,
  type Medication,
  useMedicationsState,
  savePatientMedication,
  updatePatientMedication,
} from "@/lib/dashboard/medicationsData";
import {
  getTodaysAppointment,
  useAppointmentsState,
} from "@/lib/dashboard/appointmentsData";
import {
  type Prescription,
  usePrescriptionsState,
} from "@/lib/dashboard/prescriptionsData";
import {
  defaultConsultations,
  type Consultation,
  useConsultationsState,
  fetchConsultationTimeline,
} from "@/lib/dashboard/consultationsData";
import { defaultReferrals, type Referral } from "@/lib/dashboard/referralsData";
import { type Vital, usePatientVitalsState } from "@/lib/dashboard/vitalsData";
import { getVitalKey } from "@/lib/dashboard/vitalsData";
import {
  setConsultationLoadError,
  setCurrentGlobalTabText,
  setCurrentGlobalTabValue,
  setisPatientSearched,
  setPatientInfoTab,
} from "../../dispatchers/appDispatchers";
import { ConsultationBanner } from "./components/ConsultationBanner";
import { UpcomingAppointments } from "./components/UpcomingAppointments";
import { CurrentMedications } from "./components/CurrentMedications";
import { RecentPrescriptions } from "./components/RecentPrescriptions";
import { RecentConsultations } from "./components/RecentConsultations";
import { DashboardReferralLetters } from "./components/DashboardReferralLetters";
import { DashboardPatientModals } from "./components/PatientModals";
import { VitalsSection } from "./components/VitalsSection";
import { PatientConfirmModal } from "./components/PatientConfirmModal";
import {
  setCurrentShowPatientDetailsEditModal,
  setViewOpen,
  getPatientVitalsCharts,
  getPatientAppointmentsByPatientId,
} from "@/dispatchers/patientDispatchers";
import { getPatientPrescriptionById } from "@/dispatchers/patientPrescriptionDispatcher";
import AddPatientPopUp from "../GlobalHeader/components/AddPatientPopUp";
import {
  type PatientDocument,
  addDocument,
  BackendPatientDocument,
  fileToBase64,
  usePatientDocumentsState,
  fetchDocumentById,
  removeDocument,
  renameDocument,
  emailDocuments,
  getDocumentTypeEnum,
  handleDownloadDocument,
} from "@/lib/dashboard/patientDocumentsData";
import { useModalManager } from "@/hooks/useModalManager";
import { PatientDocuments } from "./components/PatientDocuments";
import ErrorConsultationModal from "../PatientConsultation/components/ErrorConsultationModal";
import useTitanApp from "../../hooks/useTitanApp";
import { checkUserPermissions } from "@/utils/checkUserPermissions";
import {
  accessTitanverseConsultationView,
  accessTitanverseConsultationUpsert,
} from "@/data/constantStrings";
import {
  setShowPermissionModal,
  setShowPermissionMessage,
} from "@/dispatchers/appDispatchers";
import { PatientQuestionnairesPanel } from "./components/PatientQuestionnairesPanel";
import {
  Questionnaire,
  usePatienQuestionnaireData,
} from "@/lib/dashboard/questionnairesData";
import {
  PreConsultTemplate,
  usePreConsultData,
} from "@/lib/settings/preConsultData";
import { useDocumentsData } from "../Documents/documentsData";
import { MedicationHistory } from "./components/MedicationHistory";
import MultipleVaccinationPreviewModal from "./components/MultipleVaccinationPreviewModal";
import { PatientTreatmentPlans } from "./components/PatientTreatmentPlans";
import { VitalsDetailModal } from "./components/VitalsDetailMotal";
import { LinkTreatmentPlanDialog } from "./components/LinkTreatmentPlanDialog";
import { setLinkTreatmentPlanDialog } from "@/dispatchers/treatmentPlanDispatchers";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../controls/customControls/dialog";
import { Button } from "../controls/customControls/button";
import { deleteScheduleEvents } from "@/dispatchers/scheduleEventsDispatchers";
interface PatientDashboardV2Props {
  isDarkMode?: boolean;
  onBack?: () => void;
  patient?: any;
  onEditPatient?: () => void;
  onStartConsultation?: () => void;
  onEditTreatmentPlan?: (treatmentPlan: any) => void;
  onVitalSaved?: (vitalType: string, vitalValue: string) => void;
}

export function PatientDashboardV2({
  isDarkMode,
  onBack,
  patient,
  onEditPatient,
  onStartConsultation,
  onEditTreatmentPlan,
}: PatientDashboardV2Props) {
  const { appState, patientState, treatmentPlanState } = useTitanApp();
  const hasConsultationPermission =
    checkUserPermissions(accessTitanverseConsultationUpsert) ||
    checkUserPermissions(accessTitanverseConsultationView);

  const [viewingDoc, setViewingDoc] = useState<{
    title?: string;
    url?: string;
    mimeType?: string;
  } | null>(null);
  const [viewLoading, setViewLoading] = useState(false);
  const { consultationLoadError } = appState;
  const { viewOpen } = patientState;
  const {
    patientActiveTreatmentPlans,
    activeTreatmentPlansLoader,
    isLinkTreatmentPlanDialogOpen,
  } = treatmentPlanState;
  const { DashboardPatientAllergies, patientID, patientAllergies } =
    useAllergiesState();
  const { DashboardPatientConditions } = useMedicalConditionsState();
  const {
    DashboardPatientVitals,
    selectedPatientDetails,
    showPatientDetailsEditModal,
    addNewPatientVital,
    defaultVitals,
    generateHistoricalData,
  } = usePatientVitalsState();
  const {
    DashboardPatientMedications,
    patientMedications,
    patientMedicationsLoading,
    consultationId,
  } = useMedicationsState();
  const {
    prescriptions,
    medicationHistory,
    patientPrescriptionsLoader,
    onGenerateVaccineCertificate,
  } = usePrescriptionsState();
  const { appointments } = useAppointmentsState();
  const {
    consultationsUI,
    showPatientConsultationsLoader,
    setSelectedConsultation,
    selectedConsultation,
  } = useConsultationsState();
  const { mappedDocuments, currentPharmacyId } = usePatientDocumentsState();
  const { fetchDocumentById: getCompanyDocumentById } = useDocumentsData();
  const [allergies, setAllergies] = useState<Allergy[]>(
    DashboardPatientAllergies,
  );
  const [conditions, setConditions] = useState<Condition[]>();
  const [medications, setMedications] =
    useState<Medication[]>(defaultMedications);
  const [vitals, setVitals] = useState<Vital[]>(DashboardPatientVitals);
  const [hasActiveConsultation] = useState(false); // Set to true/false to test different states
  const [activeConsultation] = useState<ActiveConsultation>(
    defaultActiveConsultation,
  );
  const [selectedPrescription, setSelectedPrescription] =
    useState<Prescription | null>(null);
  const [prescriptionViewerOpen, setPrescriptionViewerOpen] = useState(false);
  const [prescriptionEditOpen, setPrescriptionEditOpen] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState<Referral | null>(
    null,
  );
  const [patientEmail, setPatientEmail] = useState(patient?.email || "");
  const [referralEditOpen, setReferralEditOpen] = useState(false);
  const [referralViewerOpen, setReferralViewerOpen] = useState(false);
  const [confirmModal, setConfirmModal] = useState({
    open: false,
    title: "",
    message: "",
    onConfirm: () => {},
    onCancel: () => {},
  });
  const [patientDocuments, setPatientDocuments] = useState<PatientDocument[]>();
  const [showPrintPreviewLoading, setShowPrintPreviewLoading] = useState(false);
  const [prescriptionImagePreview, setPrescriptionImagePreview] = useState<
    string | null
  >(null);
  const [selectedVitalForModal, setSelectedVitalForModal] =
    useState<Vital | null>(null);
  const [isVitalsDetailModalOpen, setIsVitalsDetailModalOpen] = useState(false);
  const [vitalsChartLoading, setVitalsChartLoading] = useState(false);
  const [vitalsChartData, setVitalsChartData] = useState<any | null>(null);
  const [showDeleteAppointmentModal, setshowDeleteAppointmentModal] =
    useState(false);
  const [disableDeleteAppointmentBtn, setdisableDeleteAppointmentBtn] =
    useState(false);
  const [onDeleteAppointmentID, setonDeleteAppointmentID] = useState<
    string | null
  >(null);
  const { preConsultTemplatesData } = usePreConsultData();
  const {
    questionnaires,
    sendPatientQuestionnaires,
    setQuestionnaires,
    getPatientQuestionnairesByPatientyId,
  } = usePatienQuestionnaireData();
  const modalManager = useModalManager();
  const todaysAppointment = getTodaysAppointment(appointments);
  const pharmacists = availablePharmacists;

  const handleSaveBooking = (bookingData: any) => {
    // In a real app, this would save to the backend with patient context
    const bookingWithPatient = {
      ...bookingData,
      patientName: patient?.name,
      patientEmail: patient?.email,
      patientPhone: patient?.phone,
      patientNHS: patient?.nhsNumber,
    };
  };

  const handleViewPrescription = async (prescription: Prescription) => {
    try {
      setPrescriptionViewerOpen(true);
      setPrescriptionImagePreview(" ");
      setSelectedPrescription(prescription);

      const result = await getPatientPrescriptionById(
        prescription?.prescriptionId,
      );
      setPrescriptionImagePreview(result?.base64Image || "");
    } catch (error) {
      console.error("❌ Failed to load prescription image:", error);
    }
  };
  const normalizeTrackingVitalType = (type?: string) => {
    if (!type) return type || "";
    const normalized = String(type).toLowerCase();
    const map: Record<string, string> = {
      blood_pressure: "blood-pressure",
      heart_rate: "heart-rate",
      blood_glucose: "blood-sugar",
      oxygen_saturation: "oxygen-saturation",
      respiratory_rate: "respiratory-rate",
      news_pain_score: "news-pain-score",
    };
    return map[normalized] ?? normalized;
  };

  const getVitalUnitForType = (type: string) => {
    switch (type) {
      case "heart-rate":
        return "BPM";
      case "blood-pressure":
        return "mmHg";
      case "temperature":
        return "°C";
      case "weight":
        return "kg";
      case "oxygen-saturation":
        return "%";
      case "respiratory-rate":
        return "breaths/min";
      case "news-pain-score":
        return "/10";
      case "blood-sugar":
        return "mmol/L";
      case "height":
        return "cm";
      case "circumference":
        return "cm";
      case "bmi":
        return "";
      default:
        return "";
    }
  };

  const handleVitalClickFromTreatmentPlan = async (rawVitalType: string) => {
    const vitalType = normalizeTrackingVitalType(rawVitalType);
    if (!vitalType) return;

    const normalizedVitals = (vitals || []).filter(
      (v) => normalizeTrackingVitalType(v.type) === vitalType,
    );
    const sortedVitals = [...normalizedVitals].sort(
      (a, b) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime(),
    );
    const latestVital = sortedVitals[0];

    const fallbackVital: Vital = {
      id: `tp-goal-${vitalType}-${Date.now()}`,
      type: vitalType,
      value: "-",
      unit: getVitalUnitForType(vitalType),
      label: getVitalLabel(vitalType),
      description: "No readings yet",
      timestamp: new Date(),
      historicalData: [],
    };

    setSelectedVitalForModal(latestVital || fallbackVital);
    setIsVitalsDetailModalOpen(true);
    setVitalsChartData(null);
    setVitalsChartLoading(true);

    try {
      if (patientID) {
        const chartResp = await getPatientVitalsCharts(
          patientID,
          getVitalKey(vitalType),
        );
        setVitalsChartData(chartResp || null);
      }
    } catch (error) {
      setVitalsChartData(null);
    } finally {
      setVitalsChartLoading(false);
    }
  };
  // Handle prescription editing
  const handleEditPrescription = (prescription: Prescription) => {
    setSelectedPrescription(prescription);
    setPrescriptionEditOpen(true);
  };

  const handleDeleteAppointment = async () => {
    try {
      setdisableDeleteAppointmentBtn(true);
      const targetAppointmentId = onDeleteAppointmentID;
      if (targetAppointmentId != null) {
        await deleteScheduleEvents(targetAppointmentId, currentPharmacyId);
        setshowDeleteAppointmentModal(false);
        await getPatientAppointmentsByPatientId(patientID);
      }
    } catch (ex) {
      console.log(ex);
    } finally {
      setonDeleteAppointmentID(null);
      setdisableDeleteAppointmentBtn(false);
      setshowDeleteAppointmentModal(false);
    }
  };

  const handleDeleteQuestionnaire = (id: string) => {
    setQuestionnaires((prev) =>
      prev.filter((questionnaire) => questionnaire.id !== id),
    );
  };
  const handleSendQuestionnaire = (
    questionnaire: Questionnaire,
    message: string,
    subject: string,
    email: string,
  ) => {
    // Add the questionnaire to the list
    setQuestionnaires((prev) => [questionnaire, ...prev]);

    let payload = {
      preConsultTemlateID: questionnaire.id,
      patientID: patientID,
      pharmacyID: currentPharmacyId,
      message: message,
      subject: subject,
      email: email,
    };

    sendPatientQuestionnaires(payload).then(() => {
      getPatientQuestionnairesByPatientyId(patientID);
    });

    setPatientEmail(email);
  };
  const handleStartConsultationFromQuestionnaire = (
    questionnaire: Questionnaire,
  ) => {
    if (onStartConsultation) {
      onStartConsultation();
    }
  };
  const handlePrintSummary = () => {
    const printWindow = window.open("", "_blank");
    if (!printWindow) return;

    const currentDate = new Date().toLocaleDateString();
    const currentTime = new Date().toLocaleTimeString();

    const printContent = `
      <!DOCTYPE html>
      <html>
        <head>
          <title>Patient Summary - ${patient.patientName}</title>
          <style>
            body {
              font-family: Arial, sans-serif;
              margin: 20px;
              color: #333;
            }
            .header {
              text-align: center;
              border-bottom: 2px solid #000;
              padding-bottom: 20px;
              margin-bottom: 30px;
            }
            .patient-info {
              display: grid;
              grid-template-columns: 1fr 1fr;
              gap: 20px;
              margin-bottom: 30px;
            }
            .section {
              margin-bottom: 30px;
            }
            .section-title {
              font-size: 18px;
              font-weight: bold;
              color: #2563eb;
              border-bottom: 1px solid #ddd;
              padding-bottom: 5px;
              margin-bottom: 15px;
            }
            .item {
              margin-bottom: 10px;
              padding: 10px;
              border: 1px solid #eee;
              border-radius: 5px;
            }
            .item-title {
              font-weight: bold;
              margin-bottom: 5px;
            }
            .badge {
              display: inline-block;
              padding: 2px 8px;
              border-radius: 12px;
              font-size: 12px;
              font-weight: bold;
            }
            .badge-severe { background-color: #fee2e2; color: #dc2626; }
            .badge-moderate { background-color: #fed7aa; color: #ea580c; }
            .badge-mild { background-color: #dcfce7; color: #16a34a; }
            .badge-chronic { background-color: #fed7aa; color: #ea580c; }
            .badge-active { background-color: #dcfce7; color: #16a34a; }
            .badge-acute { background-color: #fee2e2; color: #dc2626; }
            .footer {
              margin-top: 50px;
              text-align: center;
              font-size: 12px;
              color: #666;
              border-top: 1px solid #ddd;
              padding-top: 20px;
            }
            @media print {
              body { margin: 0; }
            }
          </style>
        </head>
        <body>
          <div class="header">
            <h1>Patient Summary Report</h1>
            <p>Generated on ${currentDate} at ${currentTime}</p>
          </div>

          <div class="patient-info">
            <div>
              <h2>${patient.name}</h2>
              <p><strong>Date of Birth:</strong> ${patient.dob}</p>
              <p><strong>Gender:</strong> ${patient.sex}</p>
              <p><strong>NHS Number:</strong> ${patient.nHSNumber}</p>
            </div>
            <div>
              <p><strong>Phone:</strong> ${patient?.phone}</p>
              <p><strong>Email:</strong> ${patient?.email}</p>
              <p><strong>Address:</strong> ${patient?.address}</p>
            </div>
          </div>

          <div class="section">
            <h3 class="section-title">Allergies</h3>
            ${
              allergies.length === 0
                ? "<p>No allergies recorded</p>"
                : allergies
                    .map(
                      (allergy) => `
                <div class="item">
                  <div class="item-title">${
                    allergy.allergyName
                  } <span class="badge badge-${
                    allergy.severity
                  }">${allergy.severity.toUpperCase()}</span></div>
                  ${
                    allergy.reaction
                      ? `<p><strong>Reaction:</strong> ${allergy.reaction}</p>`
                      : ""
                  }
                  ${
                    allergy.notes
                      ? `<p><strong>Notes:</strong> ${allergy.notes}</p>`
                      : ""
                  }
                </div>
              `,
                    )
                    .join("")
            }
          </div>

          <div class="section">
            <h3 class="section-title">Medical Conditions</h3>
            ${
              conditions.length === 0
                ? "<p>No medical conditions recorded</p>"
                : conditions
                    .map(
                      (condition) => `
                <div class="item">
                  <div class="item-title">${
                    condition.name
                  } <span class="badge badge-${
                    condition.status
                  }">${condition.status.toUpperCase()}</span></div>
                  ${
                    condition.diagnosedDate
                      ? `<p><strong>Diagnosed:</strong> ${new Date(
                          condition.diagnosedDate,
                        ).toLocaleDateString()}</p>`
                      : ""
                  }
                  ${
                    condition.notes
                      ? `<p><strong>Notes:</strong> ${condition.notes}</p>`
                      : ""
                  }
                </div>
              `,
                    )
                    .join("")
            }
          </div>

          <div class="section">
            <h3 class="section-title">Current Medications</h3>
            ${
              medications.length === 0
                ? "<p>No medications recorded</p>"
                : medications
                    .map(
                      (medication) => `
                <div class="item">
                  <div class="item-title">${
                    medication.item
                  } <span class="badge badge-${
                    medication.therapyLevel
                  }">${medication.therapyLevel.toUpperCase()}</span></div>
                  <p><strong>Dose:</strong> ${medication.dose}</p>
                  ${
                    medication.times
                      ? `<p><strong>Times:</strong> Morning: ${
                          medication.times.morning || "0"
                        }, Midday: ${
                          medication.times.midday || "0"
                        }, Evening: ${
                          medication.times.evening || "0"
                        }, Night: ${medication.times.night || "0"}</p>`
                      : ""
                  }
                  ${
                    medication.notes
                      ? `<p><strong>Notes:</strong> ${medication.notes}</p>`
                      : ""
                  }
                </div>
              `,
                    )
                    .join("")
            }
          </div>

          <div class="section">
            <h3 class="section-title">Latest Vitals</h3>
            ${
              vitals.length === 0
                ? "<p>No vitals recorded</p>"
                : vitals
                    .map(
                      (vital) => `
                <div class="item">
                  <div class="item-title">${vital.label}</div>
                  <p>${vital.description}</p>
                  <p><strong>Recorded:</strong> ${vital.timestamp.toLocaleDateString()}</p>
                </div>
              `,
                    )
                    .join("")
            }
          </div>

          <div class="footer">
            <p>This report was generated by TITAN Pharmacy Management System</p>
            <p>Confidential Patient Information - Handle with Care</p>
          </div>
        </body>
      </html>
    `;

    printWindow.document.write(printContent);
    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
  };

  const handleSaveAllergy = async (newAllergy: any) => {
    if (!hasConsultationPermission) {
      return;
    }

    const allergy = {
      name: newAllergy.allergyName,
      severity: newAllergy.severity,
      reaction: newAllergy.reaction || "",
      notes: newAllergy.notes || "",
    };

    if (!patientID) {
      console.error("No patient selected");
      return;
    }

    await savePatientAllergy(allergy, patientID);
  };

  const handleEditAllergy = async (index: number, updatedAllergy: any) => {
    if (!hasConsultationPermission) return;
    await updatePatientAllergy(
      index,
      updatedAllergy,
      patientID,
      patientAllergies,
    );
  };

  const handleDeleteAllergy = (index: number) => {
    if (!hasConsultationPermission) return;

    const allergyToDelete = patientAllergies[index];
    if (!allergyToDelete) return;

    setConfirmModal({
      open: true,
      title: "Delete Allergy",
      message: `Are you sure you want to delete "${allergyToDelete.allergyName}"?`,
      onConfirm: async () => {
        await deletePatientAllergy(allergyToDelete, patientID);
        setConfirmModal((prev) => ({ ...prev, open: false }));
      },
      onCancel: () => setConfirmModal((prev) => ({ ...prev, open: false })),
    });
  };

  // Handlers for conditions
  const handleSaveCondition = async (newCondition: any) => {
    if (!hasConsultationPermission) return;
    await savePatientCondition(newCondition, patientID);
  };

  const handleEditCondition = async (index: number, updatedCondition: any) => {
    if (!hasConsultationPermission) return;
    await updatePatientCondition(
      index,
      updatedCondition,
      patientID,
      DashboardPatientConditions,
    );
  };

  const handleDeleteCondition = (index: number) => {
    if (!hasConsultationPermission) return;

    const conditionToDelete = DashboardPatientConditions[index]; // from state
    if (!conditionToDelete) return;

    setConfirmModal({
      open: true,
      title: "Delete Condition",
      message: `Are you sure you want to delete "${conditionToDelete.name}"?`,
      onConfirm: async () => {
        await deletePatientCondition(conditionToDelete, patientID);
        setConfirmModal((prev) => ({ ...prev, open: false }));
      },
      onCancel: () => setConfirmModal((prev) => ({ ...prev, open: false })),
    });
  };

  // Handlers for medications
  const handleSaveMedication = async (newMedication: any) => {
    const medication = {
      id: Date.now().toString(),
      item: newMedication.item,
      dose: newMedication.dose,
      times: newMedication.times || {
        morning: "",
        midday: "",
        evening: "",
        night: "",
      },
      quantity: newMedication.quantity || "",
      measure: newMedication.measure || "",
      therapyLevel: newMedication.therapyLevel,
      preparation: newMedication.preparation || "",
      notes: newMedication.notes || "",
    };
    savePatientMedication(medication, patientID, consultationId);
    setMedications((prev) => [...prev, medication]);
  };

  const handleEditMedication = async (id: string, updatedMedication: any) => {
    const index = DashboardPatientMedications.findIndex((m) => m.id === id);
    if (index !== -1) {
      await updatePatientMedication(
        index,
        updatedMedication,
        patientID,
        consultationId,
        patientMedications,
      );
    }
  };

  const handleViewReferral = (referral: Referral) => {
    setSelectedReferral(referral);
    setReferralViewerOpen(true);
  };

  // Handle referral editing
  const handleEditReferral = (referral: Referral) => {
    setSelectedReferral(referral);
    setReferralEditOpen(true);
  };

  const handleEditFromViewer = () => {
    setPrescriptionViewerOpen(false);
    setPrescriptionEditOpen(true);
  };

  const handleSavePrescription = (prescriptionData: any) => {
    setPrescriptionEditOpen(false);
    setSelectedPrescription(null);
  };

  const handleEditReferralFromViewer = () => {
    setReferralViewerOpen(false);
    setReferralEditOpen(true);
  };

  const handleSaveReferral = (referralData: any) => {
    setReferralEditOpen(false);
    setSelectedReferral(null);
  };

  const getVitalDescription = (vital: {
    type: string;
    value: string;
    unit?: string;
  }): string => {
    switch (vital.type) {
      case "heart-rate":
        return `${vital.value} beats per minute`;
      case "weight":
        return `${vital.value} ${vital.unit}`;
      case "blood-pressure":
        return `${vital.value} ${vital.unit}`;
      case "temperature":
        return `${vital.value}${vital.unit}`;
      case "respiratory-rate":
        return `${vital.value} breaths per minute`;
      case "oxygen-saturation":
        return `${vital.value}% oxygen saturation`;
      case "news-pain-score":
        return `Pain score ${vital.value} out of 10`;
      case "blood-sugar":
        return `${vital.value} ${vital.unit} blood glucose`;
      case "height":
        return `${vital.value} ${vital.unit}`;
      case "circumference":
        return `${vital.value} body ${vital.unit} recorded`;
      case "bmi":
        return `BMI score of ${vital.value}`;
      default:
        return `${vital.value} ${vital.unit || ""}`;
    }
  };

  const getVitalLabel = (type: string): string => {
    switch (type) {
      case "heart-rate":
        return "Heart Rate";
      case "weight":
        return "Weight";
      case "blood-pressure":
        return "Blood Pressure";
      case "temperature":
        return "Temperature";
      case "respiratory-rate":
        return "Respiratory Rate";
      case "oxygen-saturation":
        return "Oxygen Saturation";
      case "news-pain-score":
        return "NEWS Pain Score";
      case "blood-sugar":
        return "Blood Sugar";
      case "height":
        return "Height";
      case "circumference":
        return "Circumference";
      case "bmi":
        return "BMI";
      default:
        return "Vital";
    }
  };

  const handleSaveVitals = async (newVital: any) => {
    // Generate sample historical data for new vitals
    // Process different vital types and extract display values
    let displayValue = "";
    let displayUnit = "";
    let displayDescription = "";

    switch (newVital.type) {
      case "bmi":
        // Calculate BMI from the form data
        const height = parseFloat(newVital.data.height);
        const weight = parseFloat(newVital.data.weight);
        const heightUnit = newVital.data.heightUnit;
        const weightUnit = newVital.data.weightUnit;

        let heightInM = height;
        let weightInKg = weight;

        // Convert height to meters
        if (heightUnit === "ft/in") {
          const feet = parseFloat(newVital.data.feet || "0");
          const inches = parseFloat(newVital.data.inches || "0");
          heightInM = (feet * 12 + inches) * 0.0254;
        } else {
          heightInM = height / 100;
        }

        // Convert weight to kg
        if (weightUnit === "lbs") {
          weightInKg = weight * 0.453592;
        }

        const bmi = weightInKg / (heightInM * heightInM);
        const bmiCategory =
          typeof bmi !== "number" || !isFinite(bmi)
            ? "Unknown"
            : bmi < 18.5
              ? "Underweight"
              : bmi < 25
                ? "Normal"
                : bmi > 30
                  ? "Overweight"
                  : "Obesity";

        displayValue = bmi.toFixed(1);
        displayUnit = "";
        displayDescription = `BMI ${bmi.toFixed(
          1,
        )} (${bmiCategory}) - Height: ${height}${
          heightUnit === "cm"
            ? "cm"
            : `'${newVital.data.feet || 0}"${newVital.data.inches || 0}`
        }, Weight: ${weight}${weightUnit}`;
        break;

      case "height":
        displayValue = newVital.data.value;
        displayUnit = newVital.data.unit;
        if (newVital.data.unit === "ft/in") {
          displayDescription = `${newVital.data.feet}'${newVital.data.inches}" (${newVital.data.value}cm equivalent)`;
        } else {
          displayDescription = `${newVital.data.value} ${newVital.data.unit}`;
        }
        break;

      case "circumference":
        const measurements = [];
        if (newVital.data.waist)
          measurements.push(
            `Waist: ${newVital.data.waist}${newVital.data.unit}`,
          );
        if (newVital.data.hip)
          measurements.push(`Hip: ${newVital.data.hip}${newVital.data.unit}`);
        if (newVital.data.neck)
          measurements.push(`Neck: ${newVital.data.neck}${newVital.data.unit}`);

        displayValue = measurements.length.toString();
        displayUnit = "measurements";
        displayDescription = measurements.join(", ");
        break;

      case "respiratory-rate":
        displayValue = newVital.data.value;
        displayUnit = "breaths/min";
        displayDescription = `${newVital.data.value} breaths per minute`;
        break;

      case "oxygen-saturation":
        displayValue = newVital.data.value;
        displayUnit = "%";
        displayDescription = `${newVital.data.value}% oxygen saturation`;
        break;

      case "news-pain-score":
        displayValue = newVital.data.value;
        displayUnit = "/10";
        displayDescription = `Pain score ${newVital.data.value} out of 10`;
        break;

      case "blood-sugar":
        displayValue = newVital.data.value;
        displayUnit = newVital.data.unit;
        displayDescription = `${newVital.data.value} ${newVital.data.unit} blood glucose`;
        break;

      default:
        // Handle traditional vitals
        displayValue = newVital.data?.value || newVital.value || "";
        displayUnit = newVital.data?.unit || newVital.unit || "";
        displayDescription = getVitalDescription({
          type: newVital.type,
          value: displayValue,
          unit: displayUnit,
        });
        break;
    }

    const vitalReading = {
      id: Date.now().toString(),
      type: newVital.type,
      value: displayValue,
      unit: displayUnit,
      label: getVitalLabel(newVital.type),
      description: displayDescription,
      timestamp: new Date(),
      historicalData: generateHistoricalData(displayValue, newVital.type),
    };

    setVitals((prev) => [...prev, vitalReading]);
    await addNewPatientVital(
      newVital,
      patientID,
      selectedPatientDetails?.patientName || "",
      DashboardPatientVitals,
    );
  };

  const extensionToMime = (ext?: string) => {
    // Ensure extension has a leading dot
    let normalizedExt = ext;
    if (normalizedExt && !normalizedExt.startsWith(".")) {
      normalizedExt = "." + normalizedExt;
    }

    switch (normalizedExt?.toLowerCase()) {
      case ".docx":
        return "application/vnd.openxmlformats-officedocument.wordprocessingml.document";
      case ".doc":
        return "application/msword";
      case ".pdf":
        return "application/pdf";
      case ".jpg":
      case ".jpeg":
        return "image/jpeg";
      case ".png":
        return "image/png";
      default:
        return "application/octet-stream";
    }
  };

  const toDataUrl = (base64: string, mime: string) =>
    `data:${mime};base64,${base64}`;

  const handleViewDocument = async (doc: any) => {
    setViewLoading(true);

    try {
      // 🔹 Case 1: Company Document
      if (doc?.downloadUrl?.startsWith("CompanyDocument")) {
        const details = await getCompanyDocumentById(doc.id, doc.title, 1);

        if (!details?.base64Content) {
          throw new Error("No company document content");
        }

        const mimeType = details.base64Content
          .split(",")[0]
          .split(":")[1]
          .split(";")[0];

        const base64Data = details.base64Content.split(",")[1];
        const byteCharacters = atob(base64Data);
        const byteNumbers = new Array(byteCharacters.length);

        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }

        const byteArray = new Uint8Array(byteNumbers);
        const blob = new Blob([byteArray], { type: mimeType });
        const blobUrl = URL.createObjectURL(blob);

        setViewingDoc({
          title: details.fileName,
          url: blobUrl,
          mimeType,
        });

        setViewOpen(true);
        return;
      }

      // 🔹 Case 2: Patient Document
      const result = await fetchDocumentById(doc.id.toString());

      if (!result?.base64Content) {
        throw new Error("No patient document content");
      }

      const mimeType = extensionToMime(result.fileExtension);

      setViewingDoc({
        title: doc.title,
        url: toDataUrl(result.base64Content, mimeType),
        mimeType,
      });

      setViewOpen(true);
    } catch (err) {
      console.error("Unable to fetch document details", err);
      setViewingDoc(null);
    } finally {
      setViewLoading(false);
    }
  };

  const handleUploadDocument = async (uploadData: {
    file: File;
    type: PatientDocument["type"];
    customName: string;
  }) => {
    try {
      const { base64File, contentType } = await fileToBase64(uploadData.file);

      const backendDoc: BackendPatientDocument = {
        patientId: patientID,
        title: uploadData.customName,
        description: `Uploaded ${uploadData?.type?.toLowerCase()} document`,
        size: `${Math.round(uploadData.file.size / 1024)} KB`,
        status: 1, // Available
        fileContent: base64File,
        contentType: contentType,
        type: getDocumentTypeEnum(uploadData.type),
        consultationId: consultationId || "",
      };

      await addDocument(backendDoc);
    } catch (error) {
      console.error("Error uploading document:", error);
    }
  };

  // Handle document rename
  const handleRenameDocument = async (documentId: number, newName: string) => {
    try {
      await renameDocument(String(documentId), newName, patientID);

      // optional local state update (for immediate UI feedback)
      setPatientDocuments((prev) =>
        prev?.map((doc) =>
          doc.id === documentId ? { ...doc, title: newName } : doc,
        ),
      );
    } catch (error) {
      // maybe show toast
      console.error("Rename failed", error);
    }
  };

  // Handle document delete
  const handleDeleteDocument = async (documentId: number) => {
    // In a real app, this would delete the document from the server

    const success = await removeDocument(String(documentId), patientID);
    if (success) {
      setPatientDocuments((prev) =>
        prev ? prev.filter((doc) => doc.id !== documentId) : [],
      );
    }
  };

  // Handle document email
  const handleEmailDocument = async (
    patientDocument: PatientDocument,
    emailBody: string,
    recipientEmail?: string,
    recipientType?: string,
  ) => {
    const finalRecipientEmail = recipientEmail || patient?.email;
    const recipientLabel =
      recipientType === "surgery"
        ? "Surgery"
        : recipientType === "other"
          ? "Custom Recipient"
          : "Patient";

    try {
      await emailDocuments(
        [String(patientDocument.id)],
        finalRecipientEmail,
        emailBody,
        currentPharmacyId,
      );

      // Optimistic note
      const emailNote = {
        id: `note-${Date.now()}`,
        content: `Document "${patientDocument.title}" emailed to ${recipientLabel} at ${finalRecipientEmail}. Message: ${emailBody}`,
        type: "communication" as const,
        author: "Current User", // replace with auth user
        timestamp: new Date(),
        communicationType: "email" as const,
        messageStatus: "sent" as const,
      };
    } catch (error) {
      console.error("Error sending email:", error);
    }
  };

  // Handle multiple documents email
  const handleEmailMultipleDocuments = async (
    documents: PatientDocument[],
    emailBody: string,
    recipientEmail?: string,
    recipientType?: string,
  ) => {
    const finalRecipientEmail = recipientEmail || patient?.email;
    const recipientLabel =
      recipientType === "surgery"
        ? "Surgery"
        : recipientType === "other"
          ? "Custom Recipient"
          : "Patient";

    try {
      await emailDocuments(
        documents.map((d) => String(d.id)),
        finalRecipientEmail,
        emailBody,
        currentPharmacyId,
      );

      // Optimistic note
      const documentList = documents.map((doc) => doc.title).join(", ");
      const emailNote = {
        id: `note-${Date.now()}`,
        content: `Documents "${documentList}" emailed to ${recipientLabel} at ${finalRecipientEmail}. Message: ${emailBody}`,
        type: "communication" as const,
        author: "Current User",
        timestamp: new Date(),
        communicationType: "email" as const,
        messageStatus: "sent" as const,
      };
    } catch (error) {
      console.error("Error sending bulk email:", error);
    }
  };
  return (
    <div className="flex flex-col h-full">
      <div
        className={`flex-1 flex transition-colors duration-300 ${
          isDarkMode ? "bg-gray-900" : "bg-gray-50"
        }`}
      >
        <PatientProfile
          patientData={patient}
          conditions={conditions}
          isDarkMode={isDarkMode}
          onEditPatient={onEditPatient}
          onPrintSummary={handlePrintSummary}
          onSaveAllergy={handleSaveAllergy}
          onEditAllergy={handleEditAllergy}
          onDeleteAllergy={handleDeleteAllergy}
          onSaveCondition={handleSaveCondition}
          onEditCondition={handleEditCondition}
          onDeleteCondition={handleDeleteCondition}
          hasConsultationPermission={hasConsultationPermission}
        />
        <div
          className={`flex-1 max-h-[calc(100vh-150px)] overflow-y-auto transition-colors duration-300 scrollbar-hover-thin ${
            isDarkMode ? "bg-gray-900" : "bg-gray-50"
          }`}
        >
          <div className="p-6">
            <VitalsSection
              vitals={DashboardPatientVitals}
              isDarkMode={isDarkMode}
              onSaveVitals={
                hasConsultationPermission ? handleSaveVitals : undefined
              }
              hasConsultationPermission={hasConsultationPermission}
              onPermissionDenied={() => {
                setShowPermissionModal(true);
                setShowPermissionMessage(
                  "You don't have permission to add or edit vitals.",
                );
              }}
            />
            {/* Vitals Section - Full Width */}
            <ConsultationBanner
              hasActiveConsultation={hasActiveConsultation}
              activeConsultation={activeConsultation}
              todaysAppointment={todaysAppointment}
              isDarkMode={isDarkMode}
              onStartConsultation={onStartConsultation}
            />

            {/* Treatment Plans Panel */}
            {patientActiveTreatmentPlans?.length > 0 && (
                <PatientTreatmentPlans
                  treatmentPlans={patientActiveTreatmentPlans}
                  isDarkMode={isDarkMode}
                  isLoading={activeTreatmentPlansLoader}
                  // onStartConsultation={(treatmentPlanId) => {
                  //   onStartConsultation?.(treatmentPlanId);
                  // }}
                  onEditTreatmentPlan={onEditTreatmentPlan}
                  appointments={appointments}
                  consultations={defaultConsultations}
                  vitals={vitals}
                  onVitalClick={handleVitalClickFromTreatmentPlan}
                />
              )}

            <h2
              className={`text-lg font-semibold mb-6 transition-colors duration-300 mt-6 ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              Patient History & Schedule
            </h2>
            <div className="grid grid-cols-2 gap-6">
              <UpcomingAppointments
                appointments={appointments}
                pharmacists={pharmacists}
                isDarkMode={isDarkMode}
                onSaveBooking={handleSaveBooking}
                showAppointmentDeleteModal={setshowDeleteAppointmentModal}
                onDeleteAppointmentID={setonDeleteAppointmentID}
              />
              <MedicationHistory
                history={medicationHistory}
                isDarkMode={isDarkMode}
                patientPrescriptionsLoader={patientPrescriptionsLoader}
                onGenerateVaccineCertificate={onGenerateVaccineCertificate}
              />
              <CurrentMedications
                medications={DashboardPatientMedications}
                isDarkMode={isDarkMode}
                onSaveMedication={handleSaveMedication}
                onEditMedication={handleEditMedication}
              />
              <RecentPrescriptions
                prescriptions={prescriptions}
                isDarkMode={isDarkMode}
                onViewPrescription={handleViewPrescription}
                onEditPrescription={handleEditPrescription}
              />
              <RecentConsultations
                consultations={consultationsUI}
                isDarkMode={isDarkMode}
                onSelectConsultation={(consultation) => {
                  setSelectedConsultation(consultation);
                  fetchConsultationTimeline(consultation.consultationId);
                }}
              />
              <PatientDocuments
                key={`documents-${mappedDocuments.length}-${mappedDocuments
                  .map((d) => d.id)
                  .join("-")}`}
                documents={mappedDocuments}
                isDarkMode={isDarkMode}
                onViewDocument={handleViewDocument}
                onDownloadDocument={handleDownloadDocument}
                onUploadDocument={handleUploadDocument}
                onRenameDocument={handleRenameDocument}
                onDeleteDocument={handleDeleteDocument}
                onEmailDocument={handleEmailDocument}
                onEmailMultipleDocuments={handleEmailMultipleDocuments}
                patientEmail={patient?.email}
                modalManager={modalManager}
              />
              {/* Patient Questionnaires Panel */}
              <PatientQuestionnairesPanel
                questionnaires={questionnaires}
                patientName={patient?.name}
                preConsultTemplates={preConsultTemplatesData}
                isDarkMode={isDarkMode}
                onDeleteQuestionnaire={handleDeleteQuestionnaire}
                onSendQuestionnaire={handleSendQuestionnaire}
                onStartConsultation={handleStartConsultationFromQuestionnaire}
                patientEmail={patient?.email}
                setPatientEmail={setPatientEmail}
              />
            </div>
          </div>
        </div>
      </div>
      <DashboardPatientModals
        prescriptionViewerOpen={prescriptionViewerOpen}
        setPrescriptionViewerOpen={setPrescriptionViewerOpen}
        prescriptionEditOpen={prescriptionEditOpen}
        setPrescriptionEditOpen={setPrescriptionEditOpen}
        selectedPrescription={selectedPrescription}
        setSelectedPrescription={setSelectedPrescription}
        referralViewerOpen={referralViewerOpen}
        setReferralViewerOpen={setReferralViewerOpen}
        referralEditOpen={referralEditOpen}
        setReferralEditOpen={setReferralEditOpen}
        selectedReferral={selectedReferral}
        setSelectedReferral={setSelectedReferral}
        selectedConsultation={selectedConsultation}
        setSelectedConsultation={setSelectedConsultation}
        onEditFromViewer={handleEditFromViewer}
        onSavePrescription={handleSavePrescription}
        onEditReferralFromViewer={handleEditReferralFromViewer}
        onSaveReferral={handleSaveReferral}
        patient={patient}
        patientData={patient}
        isDarkMode={isDarkMode}
        prescriptionImagePreview={prescriptionImagePreview}
      />
      <PatientConfirmModal
        open={confirmModal.open}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={confirmModal.onCancel}
      />
      {showPatientDetailsEditModal && (
        <AddPatientPopUp
          open={showPatientDetailsEditModal}
          onOpenChange={setCurrentShowPatientDetailsEditModal}
          onSave={(patient) => {}}
          isDarkMode={isDarkMode}
          editMode={selectedPatientDetails}
        />
      )}
      {consultationLoadError && (
        <ErrorConsultationModal
          open={consultationLoadError}
          onClose={() => setConsultationLoadError(false)}
        />
      )}
      <ViewDocumentModal
        open={viewOpen}
        onClose={() => setViewOpen(false)}
        title={viewingDoc?.title}
        documentUrl={viewingDoc?.url}
        loading={viewLoading}
        mimeType={viewingDoc?.mimeType}
      />
      <VitalsDetailModal
        isOpen={isVitalsDetailModalOpen}
        onOpenChange={(open) => {
          setIsVitalsDetailModalOpen(open);
          if (!open) {
            setVitalsChartData(null);
            setVitalsChartLoading(false);
          }
        }}
        vital={selectedVitalForModal}
        isDarkMode={isDarkMode}
        loading={vitalsChartLoading}
        chartData={vitalsChartData}
      />

      <MultipleVaccinationPreviewModal />
      <LinkTreatmentPlanDialog
        open={isLinkTreatmentPlanDialogOpen}
        onOpenChange={(open) => setLinkTreatmentPlanDialog(open)}
        activePlans={patientActiveTreatmentPlans || []}
        appointments={appointments}
        onSelectPlan={() => {
          // TODO later
        }}
        onSkip={() => {
          // TODO later
        }}
        isDarkMode={isDarkMode}
        fromConsultataion={false}
      />

      {/* Delete Appointment Modal */}
      <Dialog
        open={showDeleteAppointmentModal}
        onOpenChange={setshowDeleteAppointmentModal}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Delete Appointment</DialogTitle>
          </DialogHeader>

          <div
            className={`py-4 ${
              isDarkMode ? "text-gray-300" : "text-gray-700"
            } mb-6`}
          >
            Are you sure you want to delete this appointment? This cannot be
            undone.
          </div>

          {/* FOOTER */}
          <div className="flex justify-end gap-2 pt-4 border-t">
            <Button
              onClick={() => setshowDeleteAppointmentModal(false)}
              className="w-full text-white bg-black"
            >
              Cancel
            </Button>

            <Button
              onClick={handleDeleteAppointment}
              disabled={disableDeleteAppointmentBtn}
              className="w-full text-white bg-red-600 hover:bg-red-700"
            >
              {disableDeleteAppointmentBtn ? "Deleting..." : "Delete"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
